import { useState } from "react";
import { X } from "lucide-react";
import { CampoDaConsulta, bareInputCls } from "./CampoDaConsulta";
import { BuscaDeServicos, type ServicoDoCatalogo } from "./BuscaDeServicos";

const linkCls = "font-semibold text-brand-600 hover:underline dark:text-primary";

/**
 * Serviços da consulta: busca, os escolhidos como etiquetas e o texto que aparece no card. O texto
 * nasce dos serviços e fica recolhido; continua editável porque é o que aparece no card, na ficha e
 * no {procedimento} do lembrete ("Restauração — dente 26"). Sem catálogo, sobra só o texto.
 */
export function ServicosDaConsulta({
  comCatalogo,
  servicos,
  selecionados,
  atribuidos,
  texto,
  textoDosServicos,
  onAlternar,
  onTexto,
  onLimpar,
  onCatalogoMudou,
}: {
  comCatalogo: boolean;
  servicos: ServicoDoCatalogo[];
  selecionados: string[];
  atribuidos: string[];
  texto: string;
  /** O texto que os serviços escolhidos gerariam — diferente do atual = foi editado à mão. */
  textoDosServicos: string;
  onAlternar: (id: string) => void;
  onTexto: (t: string) => void;
  onLimpar: () => void;
  onCatalogoMudou: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const escolhidos = selecionados.map((id) => servicos.find((s) => s.id === id)).filter((s): s is ServicoDoCatalogo => !!s);
  const textoProprio = texto.trim() !== "" && texto !== textoDosServicos;
  const mostrarTexto = !comCatalogo || editando || textoProprio;
  const algo = selecionados.length > 0 || texto.trim() !== "";

  const campoDeTexto = (
    <input
      className={bareInputCls}
      value={texto}
      onChange={(e) => onTexto(e.target.value)}
      autoFocus={comCatalogo && editando}
      placeholder={comCatalogo ? "Como aparece no card da agenda" : "Descreva o procedimento"}
    />
  );

  if (!comCatalogo) return <CampoDaConsulta label="Procedimento">{campoDeTexto}</CampoDaConsulta>;

  return (
    <CampoDaConsulta
      comoDiv
      label="Procedimentos"
      action={
        algo ? (
          <button
            type="button"
            onClick={() => {
              onLimpar();
              setEditando(false);
            }}
            className="inline-flex items-center gap-1 rounded text-[10px] font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:text-zinc-700 dark:text-muted-foreground dark:hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Limpar
          </button>
        ) : null
      }
    >
      <BuscaDeServicos servicos={servicos} selecionados={selecionados} atribuidos={atribuidos} onEscolher={onAlternar} onCriado={onCatalogoMudou} />

      {escolhidos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {escolhidos.map((s) => (
            <span
              key={s.id}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 py-1 pl-2 pr-1 text-[12px] text-zinc-800 dark:border-border dark:bg-muted dark:text-foreground"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400" style={s.color ? { backgroundColor: s.color } : undefined} />
              <span className="truncate font-medium">{s.name}</span>
              {s.durationMinutes ? <span className="shrink-0 text-zinc-400 dark:text-muted-foreground">{s.durationMinutes} min</span> : null}
              <button
                type="button"
                onClick={() => onAlternar(s.id)}
                aria-label={`Tirar ${s.name}`}
                className="rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-border dark:hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {mostrarTexto ? (
        <div className="mt-2 border-t border-zinc-100 pt-2 dark:border-border/60">
          <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70">Texto do card</span>
          {campoDeTexto}
        </div>
      ) : (
        <p className="mt-2 truncate text-[11.5px] text-zinc-500 dark:text-muted-foreground">
          {texto.trim() ? (
            <>
              No card: <span className="font-medium text-zinc-700 dark:text-foreground">{texto}</span> ·{" "}
            </>
          ) : null}
          <button type="button" onClick={() => setEditando(true)} className={linkCls}>
            {texto.trim() ? "Editar" : "Descrever sem o catálogo"}
          </button>
        </p>
      )}
    </CampoDaConsulta>
  );
}
